// pkg21 — firm-private AI modules merged into the client modules context (firmModules list) — delivered 2026-06-11
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../App';
import { api } from '../utils/api';

// Provides the list of modules visible to the currently signed-in client,
// fetched from GET /api/modules/me. Used by:
//   * components/Layout.js     — sidebar links
//   * pages/DashboardPage.js   — dashboard cards
//   * pages/ContentModulePage  — viewer pages for content_pdf / content_html
//
// pkg21: ALSO provides `firmModules` — the firm-private AI modules assigned
// to this client (GET /api/firm-modules/me). These are a separate system
// from the platform module catalog (firm_modules tables, AiModulePage at
// /ai-modules/:id), so they ride in their own array rather than being mixed
// into `modules`. A failure fetching firm modules never blocks the platform
// modules list (and vice versa) — each fetch fails soft to [].
//
// Re-fetches when the auth token changes (login / logout). Children can call
// refresh() to force a re-fetch (currently unused but useful if the admin
// changes assignments while the client is logged in and we want to expose a
// "refresh modules" affordance later).

const ClientModulesContext = createContext({
  modules: [],
  firmModules: [],
  loading: true,
  error: null,
  refresh: () => Promise.resolve(),
});

export function ClientModulesProvider({ children }) {
  const { token } = useAuth();
  const [modules,     setModules]     = useState([]);
  const [firmModules, setFirmModules] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  const refresh = useCallback(async () => {
    if (!token) {
      setModules([]);
      setFirmModules([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // Fetch both lists in parallel; each fails soft so one system's outage
    // can't blank the other's sidebar links.
    const [platform, firm] = await Promise.all([
      api.getMyModules().catch((err) => { setError(err); return []; }),
      api.getMyFirmModules().catch(() => []),
    ]);
    setModules(Array.isArray(platform) ? platform : []);
    setFirmModules(Array.isArray(firm) ? firm : []);
    setLoading(false);
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <ClientModulesContext.Provider value={{ modules, firmModules, loading, error, refresh }}>
      {children}
    </ClientModulesContext.Provider>
  );
}

export function useClientModules() {
  return useContext(ClientModulesContext);
}
